import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Card,
    CardContent,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Button,
    Grid,
    Typography,
    TablePagination,
    Stack
} from '@mui/material';
import SearchableSelect from '../../components/SearchableSelect';
import { PictureAsPdf as PictureAsPdfIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import inventarioInformes from '../../services/inventarioInformes';
import InformesBackButton from '../../components/shared/InformesBackButton';

interface StockDeposito {
    codigoArticulo: string;
    descripcion: string;
    nroUbicacion: number;
    nombreUbicacion: string;
    cantidadDeposito: number;
    cantidadAsignada: number;
    disponibleDeposito: number;
}

interface Ubicacion {
    nroUbicacion: number;
    nombreUbicacion: string;
}


const StockPorDeposito: React.FC = () => {
    const navigate = useNavigate();
    const [stock, setStock] = useState<StockDeposito[]>([]);
    const [ubicaciones, setUbicaciones] = useState<Ubicacion[]>([]);
    const [filters, setFilters] = useState({
        codigoArticulo: '',
        descripcion: '',
        nroUbicacion: ''
    });
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const { enqueueSnackbar } = useSnackbar();

    // Cargar ubicaciones al montar el componente
    useEffect(() => {
        const loadUbicaciones = async () => {
            try {
                const data = await inventarioInformes.getUbicaciones();
                setUbicaciones(data);
            } catch (error) {
                console.error('Error al cargar ubicaciones:', error);
                enqueueSnackbar('Error al cargar las ubicaciones', { variant: 'error' });
            }
        };
        loadUbicaciones();
    }, []);

    const ubicacionOptions = useMemo(
        () =>
            ubicaciones.map((ubicacion) => ({
                value: ubicacion.nroUbicacion,
                label: `${ubicacion.nroUbicacion} - ${ubicacion.nombreUbicacion}`,
            })),
        [ubicaciones],
    );

    const loadData = async () => {
        try {
            setLoading(true);
            const response = await inventarioInformes.getStockPorDeposito({
                codigoArticulo: filters.codigoArticulo || undefined,
                descripcion: filters.descripcion || undefined,
                nroUbicacion: filters.nroUbicacion ? Number(filters.nroUbicacion) : undefined,
                page: page + 1,
                pageSize: rowsPerPage
            });
            setStock(response.data);
            setTotalCount(response.totalCount);
        } catch (error) {
            console.error('Error al cargar datos:', error);
            enqueueSnackbar('Error al cargar los datos', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleGeneratePdf = async () => {
        try {
            setLoading(true);
            const blob = await inventarioInformes.getStockPorDepositoPdf({
                codigoArticulo: filters.codigoArticulo || undefined,
                descripcion: filters.descripcion || undefined,
                nroUbicacion: filters.nroUbicacion ? Number(filters.nroUbicacion) : undefined
            });
            
            if (blob.size === 0) {
                enqueueSnackbar('No hay datos para generar el PDF', { variant: 'warning' });
                return;
            }
            
            const url = window.URL.createObjectURL(blob);
            window.open(url, '_blank');
        } catch (error: any) {
            console.error('Error al generar PDF:', error);
            const errorMessage = error.response?.data?.error || 'Error al generar el PDF';
            enqueueSnackbar(errorMessage, { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, [page, rowsPerPage]);

    const handleSearch = () => {
        setPage(0);
        loadData();
    };

    const handleRowNavigateMovimientos = (row: StockDeposito) => {
        const params = new URLSearchParams();
        params.set('codigoArticulo', row.codigoArticulo);
        params.set('deposito', String(row.nroUbicacion));
        navigate(`/inventario/informes/movimientos-stock?${params.toString()}`);
    };

    return (
        <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
            <Card
                sx={{
                    bgcolor: '#ffffff',
                    borderRadius: 3,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    overflow: 'hidden',
                }}
            >
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ mb: 3 }}
                    >
                        <InformesBackButton to="/inventario/informes" useBrowserBack />
                        <Typography
                            variant="h4"
                            sx={{ fontWeight: 600, color: 'primary.main' }}
                        >
                            Stock por Depósito
                        </Typography>
                    </Stack>

                    <Card sx={{ mb: 3, bgcolor: '#fafafa' }}>
                        <CardContent>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        label="Código Artículo"
                                        value={filters.codigoArticulo}
                                        onChange={(e) => setFilters({ ...filters, codigoArticulo: e.target.value })}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        label="Descripción"
                                        value={filters.descripcion}
                                        onChange={(e) => setFilters({ ...filters, descripcion: e.target.value })}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <SearchableSelect<number>
                                        label="Ubicación"
                                        value={filters.nroUbicacion ? Number(filters.nroUbicacion) : null}
                                        onChange={(val) =>
                                            setFilters({
                                                ...filters,
                                                nroUbicacion: val != null ? String(val) : '',
                                            })
                                        }
                                        options={ubicacionOptions}
                                        allowEmpty
                                        emptyOptionLabel="Todas"
                                    />
                                </Grid>
                            </Grid>
                            <Box mt={2} display="flex" gap={2}>
                                <Button
                                    variant="contained"
                                    onClick={handleSearch}
                                    disabled={loading}
                                >
                                    Buscar
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    startIcon={<PictureAsPdfIcon />}
                                    onClick={handleGeneratePdf}
                                    disabled={loading}
                                    sx={{
                                        borderRadius: 2,
                                        px: { xs: 2, sm: 3 },
                                        py: 1,
                                        textTransform: 'none',
                                        fontWeight: 500,
                                    }}
                                >
                                    Generar PDF
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>

                    <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Código</TableCell>
                            <TableCell>Descripción</TableCell>
                            <TableCell>Nro. Ubicación</TableCell>
                            <TableCell>Nombre Ubicación</TableCell>
                            <TableCell align="right">Cantidad</TableCell>
                            <TableCell align="right">Asignado</TableCell>
                            <TableCell align="right">Disponible</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {stock && stock.length > 0 ? (
                            stock.map((row) => (
                                <TableRow
                                    key={`${row.codigoArticulo}-${row.nroUbicacion}`}
                                    hover
                                    onClick={() => handleRowNavigateMovimientos(row)}
                                    sx={{ cursor: 'pointer' }}
                                >
                                    <TableCell>{row.codigoArticulo}</TableCell>
                                    <TableCell>{row.descripcion}</TableCell>
                                    <TableCell>{row.nroUbicacion}</TableCell>
                                    <TableCell>{row.nombreUbicacion}</TableCell>
                                    <TableCell align="right">{row.cantidadDeposito?.toFixed(2)}</TableCell>
                                    <TableCell align="right">{row.cantidadAsignada?.toFixed(2)}</TableCell>
                                    <TableCell align="right">{row.disponibleDeposito?.toFixed(2)}</TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    No hay datos disponibles
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage="Filas por página"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                />
            </TableContainer>
                </CardContent>
            </Card>
        </Box>
    );
};

export default StockPorDeposito;
